import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import { getLayoutSlotCount } from '../utils/multiWindowLive';
import ResourceManager from '../components/multiWindowLive/ResourceManager';

/**
 * 多窗口流 CRUD、拖拽换窗、自动填充、清空、布局截断。
 * 保持 slot 稳定 null 空洞，不重新排序。
 *
 * @param {object} opts
 * @param {object} opts.effectiveLayout
 * @param {string} opts.currentLayout
 * @param {boolean} opts.isMobileLayout
 * @param {Array} opts.availableStreams
 * @param {React.MutableRefObject} opts.streamsRef 外壳共享 ref（可见性 hook 读最新流）
 * @param {Function} opts.setStreamStates 静默清空时一并重置
 */
export default function useMultiWindowStreams({
  effectiveLayout,
  currentLayout,
  isMobileLayout,
  availableStreams,
  streamsRef,
  setStreamStates,
  memberMetaByKey = {},
}) {
  const [streams, setStreams] = useState([]);
  /** 鼠标拖拽换窗：源窗口 / 当前悬停窗口 */
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // 同步最新 streams 到共享 ref，供可见性定时器读取
  streamsRef.current = streams;

  /**
   * 添加流到指定窗口。
   * 必须落在点击的 targetIndex，禁止为「数组还不够长」而改塞到前面空位。
   */
  const addStream = (streamKey, memberName, protocol = 'webrtc', targetIndex = -1) => {
    if (streams.some((s) => s && s.streamKey === streamKey)) {
      message.warning(`流 ${streamKey} 已在其他窗口播放`);
      console.log(`流 ${streamKey} 已存在，跳过添加`);
      return;
    }

    if (!ResourceManager.canStartNewPlayer()) {
      message.warning(`已达到最大播放设备数量限制(${ResourceManager.maxConcurrentPlayers})，无法添加更多流`);
      return;
    }

    const maxSlots = getLayoutSlotCount(effectiveLayout);
    const newStream = {
      streamKey,
      memberName,
      protocol,
      id: `${streamKey}_${Date.now()}`
    };

    setStreams((prev) => {
      const newStreams = [...prev];
      while (newStreams.length < maxSlots) {
        newStreams.push(null);
      }

      // 点击了具体窗口：只放该位，绝不自动前移排序
      if (targetIndex >= 0 && targetIndex < maxSlots) {
        if (newStreams[targetIndex]) {
          message.warning('此窗口已有流，请先移除现有流');
          return prev;
        }
        newStreams[targetIndex] = newStream;
        console.log(`在点击窗口 ${targetIndex} 添加流: ${streamKey}，协议: ${protocol}`);
        return newStreams;
      }

      // 未指定窗口（兼容旧调用）：才用第一个空位
      const emptyIndex = newStreams.findIndex((s) => s === null);
      if (emptyIndex >= 0) {
        newStreams[emptyIndex] = newStream;
        console.log(`未指定窗口，填入空位 ${emptyIndex}: ${streamKey}`);
        return newStreams;
      }
      console.log(`无空位，忽略添加: ${streamKey}`);
      return prev;
    });
  };

  /** 鼠标拖拽：将 from 窗口的画面移到 to（目标有流则互换） */
  const moveStreamBetweenSlots = useCallback((fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return;
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

    setStreams((prev) => {
      const maxSlots = getLayoutSlotCount(effectiveLayout);
      if (fromIndex >= maxSlots || toIndex >= maxSlots) return prev;
      const next = [...prev];
      while (next.length < maxSlots) next.push(null);
      const fromStream = next[fromIndex];
      if (!fromStream) return prev;
      const toStream = next[toIndex];
      next[toIndex] = fromStream;
      next[fromIndex] = toStream || null;
      console.log(
        toStream
          ? `互换窗口 ${fromIndex} ↔ ${toIndex}`
          : `画面从窗口 ${fromIndex} 移到 ${toIndex}`
      );
      return next;
    });
  }, [effectiveLayout]);

  // 移除流 - 保持位置固定版本，不重新排序
  const removeStream = (index) => {
    const streamToRemove = streams[index];
    if (streamToRemove) {
      // 确保从资源管理器中注销
      ResourceManager.unregisterPlayer(streamToRemove.streamKey);
      console.log(`移除流: ${streamToRemove.streamKey}，已从资源管理器注销`);
    }

    // 延迟更新状态，确保DOM完全清理
    setTimeout(() => {
      const newStreams = [...streams];
      newStreams[index] = null; // 设置为null而不是删除元素，保持位置
      setStreams(newStreams);
      console.log(`流已移除，位置${index}现在为空，窗口位置保持不变`);
    }, 100); // 100ms延迟确保资源清理完成
  };

  // 更新流协议
  const updateStreamProtocol = (index, protocol) => {
    const newStreams = [...streams];
    if (newStreams[index]) { // 确保不是null
      newStreams[index].protocol = protocol;
      setStreams(newStreams);
    }
  };

  // 清空所有流
  const clearAllStreams = () => {
    Modal.confirm({
      title: '确定要清空所有流吗？',
      onOk: () => {
        // 清理所有注册的播放器
        streams.forEach(stream => {
          if (stream) {
            ResourceManager.unregisterPlayer(stream.streamKey);
          }
        });
        setStreams([]);
      }
    });
  };

  // 静默移除所有流（不显示确认对话框）
  const clearAllStreamsSilently = useCallback(() => {
    console.log('静默移除所有流');

    setStreams(currentStreams => {
      // 清理所有注册的播放器
      currentStreams.forEach(stream => {
        if (stream) {
          ResourceManager.unregisterPlayer(stream.streamKey);
          console.log(`静默移除流: ${stream.streamKey}`);
        }
      });

      // 清空流状态
      setStreamStates(new Map());

      return [];
    });
  }, [setStreamStates]);

  // 自动填充流 - 改进版本，优先填充空位置，保持窗口位置固定
  const autoFillStreams = () => {
    const maxStreams = getLayoutSlotCount(effectiveLayout);
    const availableKeys = availableStreams.map(s => s.streamKey);
    const currentKeys = streams.filter(s => s !== null).map(s => s.streamKey);
    const newKeys = availableKeys.filter(key => !currentKeys.includes(key));

    // 计算空位置数量和可添加数量
    const emptySlots = [];
    for (let i = 0; i < maxStreams; i++) {
      if (i >= streams.length || streams[i] === null) {
        emptySlots.push(i);
      }
    }

    const canAddCount = Math.min(
      emptySlots.length, // 空位置数量
      newKeys.length, // 可用流数量
      ResourceManager.maxConcurrentPlayers - ResourceManager.getActiveCount() // 资源限制
    );

    if (canAddCount <= 0) {
      if (ResourceManager.getActiveCount() >= ResourceManager.maxConcurrentPlayers) {
        message.warning(`已达到最大播放设备数量限制(${ResourceManager.maxConcurrentPlayers})，无法自动填充更多流`);
      } else if (newKeys.length === 0) {
        message.info('没有新的可用流可以填充');
      } else if (emptySlots.length === 0) {
        message.info('所有窗口位置都已占用');
      }
      return;
    }

    // 扩展streams数组以容纳所有位置
    const newStreams = [...streams];
    while (newStreams.length < maxStreams) {
      newStreams.push(null);
    }

    // 在空位置填充新流
    for (let i = 0; i < canAddCount; i++) {
      const targetIndex = emptySlots[i];
      // 保持原始streamKey，不进行大小写转换
      const originalStreamKey = newKeys[i];
      newStreams[targetIndex] = {
        streamKey: originalStreamKey,
        memberName: (memberMetaByKey[newKeys[i]]?.name || memberMetaByKey[String(newKeys[i] || '').trim()]?.name || newKeys[i]),
        protocol: 'webrtc',
        id: `${originalStreamKey}_${Date.now()}_${i}` // 添加唯一ID
      };
    }

    console.log(`自动填充完成，在位置 [${emptySlots.slice(0, canAddCount).join(', ')}] 添加了 ${canAddCount} 个流`);
    setStreams(newStreams);
  };

  // 布局改变时调整流数量，保持现有位置不变
  useEffect(() => {
    const maxStreams = getLayoutSlotCount(effectiveLayout);
    if (streams.length > maxStreams) {
      // 只截断超出的部分，保持前面位置的对象不变
      const truncatedStreams = streams.slice(0, maxStreams);

      // 清理被截断的流的资源
      for (let i = maxStreams; i < streams.length; i++) {
        if (streams[i]) {
          ResourceManager.unregisterPlayer(streams[i].streamKey);
          console.log(`布局变更：清理位置${i}的流 ${streams[i].streamKey}`);
        }
      }

      setStreams(truncatedStreams);
      console.log(`布局变更：从${streams.length}个位置截断到${maxStreams}个位置`);
    }
  }, [currentLayout, isMobileLayout, effectiveLayout.slots, effectiveLayout.cols, effectiveLayout.rows, streams]);

  return {
    streams,
    setStreams,
    dragFromIndex,
    setDragFromIndex,
    dragOverIndex,
    setDragOverIndex,
    addStream,
    moveStreamBetweenSlots,
    removeStream,
    updateStreamProtocol,
    clearAllStreams,
    clearAllStreamsSilently,
    autoFillStreams
  };
}
